import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useState } from "react";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { ActorFilter } from "./constants";
import { PropertyActivityLogResponse } from "./types";

const buildParams = (
  actorFilter: ActorFilter,
  search: string,
  page: number,
  unitId?: number
) => {
  const query: Record<string, string | number> = { page };
  if (actorFilter !== ActorFilter.ALL) query.actor_type = actorFilter;
  if (search) query.search = search;
  if (unitId != null) query.unit_id = unitId;
  return query;
};

const propertyActivityLogQueryKey = (
  propertyId: string,
  unitId: number | undefined,
  actorFilter: ActorFilter,
  search: string,
  page: number
) => ["PROPERTY_ACTIVITY_LOG", propertyId, unitId, actorFilter, search, page];

export const usePropertyActivityLogController = ({
  propertyId,
  unitId,
  enabled = true
}: {
  propertyId?: string;
  unitId?: number;
  enabled?: boolean;
}) => {
  const [actorFilter, setActorFilterState] = useState<ActorFilter>(
    ActorFilter.ALL
  );
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQueryState] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 400);

  const setActorFilter = (next: ActorFilter) => {
    setActorFilterState(next);
    setPage(1);
  };

  // Search is server-side, so it must reset pagination like actorFilter does.
  const setSearchQuery = (next: string) => {
    setSearchQueryState(next);
    setPage(1);
  };

  const activityLogMeta = useQuery<PropertyActivityLogResponse>({
    queryKey: propertyActivityLogQueryKey(
      String(propertyId),
      unitId,
      actorFilter,
      debouncedSearchQuery,
      page
    ),
    queryFn: () =>
      instance
        .get(`portal/v1/properties/${propertyId}/activity_log`, {
          params: buildParams(actorFilter, debouncedSearchQuery, page, unitId)
        })
        .then((res) => res.data),
    enabled: Boolean(propertyId) && enabled,
    placeholderData: keepPreviousData
  });

  const cards = activityLogMeta.data?.activity_log ?? [];
  const pagination = activityLogMeta.data?.pagination;

  return {
    activityLogMeta,
    cards,
    pagination,
    actorFilter,
    setActorFilter,
    page,
    setPage,
    searchQuery,
    setSearchQuery
  };
};
